import React from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { XCircle, AlertTriangle, ArrowLeft, RefreshCw } from 'lucide-react';

const MembershipFailed: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const orderId = searchParams.get('orderId') || '';
  const rawReason = searchParams.get('reason') || 'Payment could not be processed';
  const errorCode = searchParams.get('code') || '';
  const amount = searchParams.get('amount') || '';

  // Format user-friendly error message
  let displayError = rawReason;
  let errorHint = 'Please check your card details, expiration date, CVV, or try a different card.';

  if (errorCode.includes('EXPIREDCARD') || rawReason.includes('EXPIREDCARD') || errorCode.includes('54-')) {
    displayError = 'Card Expired (Decline Code: N:54-EXPIREDCARD)';
    errorHint = 'The card entered has expired or has an invalid expiration date. Please use a valid card.';
  } else if (rawReason.includes('DECLINED') || errorCode.startsWith('N:')) {
    displayError = `Card Declined (${errorCode || rawReason})`;
    errorHint = 'Your issuing bank declined the transaction. Please check with your bank or try another card.';
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8fafc', padding: '40px 16px' }}>
      <div
        style={{
          maxWidth: '560px',
          width: '100%',
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          border: '1px solid #fee2e2',
          boxShadow: '0 10px 25px -5px rgba(239, 68, 68, 0.1), 0 8px 10px -6px rgba(239, 68, 68, 0.1)',
          overflow: 'hidden'
        }}
      >
        {/* Header Banner */}
        <div
          style={{
            background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
            color: '#ffffff',
            padding: '28px 24px',
            textAlign: 'center'
          }}
        >
          <div
            style={{
              width: '64px',
              height: '64px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '50%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '12px'
            }}
          >
            <XCircle size={36} color="#ffffff" />
          </div>
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 800 }}>Payment Unsuccessful</h1>
          <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#fee2e2' }}>
            Your transaction could not be completed by the gateway.
          </p>
        </div>

        {/* Body Content */}
        <div style={{ padding: '28px 24px' }}>
          {/* Error Details Box */}
          <div
            style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '10px',
              padding: '16px',
              marginBottom: '20px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#991b1b', fontWeight: 700, fontSize: '14px' }}>
              <AlertTriangle size={18} />
              <span>Decline Details</span>
            </div>
            <div style={{ fontSize: '14px', color: '#b91c1c', fontWeight: 600, marginTop: '6px' }}>
              {displayError}
            </div>
            <div style={{ fontSize: '12px', color: '#7f1d1d', marginTop: '4px', lineHeight: 1.5 }}>
              {errorHint}
            </div>
          </div>

          {/* Transaction Reference Table */}
          {orderId && (
            <div
              style={{
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                padding: '14px 16px',
                marginBottom: '24px'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '5px 0', borderBottom: '1px solid #e2e8f0' }}>
                <span style={{ color: '#64748b' }}>Company Trade Name:</span>
                <span style={{ fontWeight: 600, color: '#1e293b' }}>{searchParams.get('tradeName') || 'SkillSeek Limited'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '5px 0', borderBottom: '1px solid #e2e8f0' }}>
                <span style={{ color: '#64748b' }}>Order Reference:</span>
                <span style={{ fontWeight: 600, color: '#1e293b', fontFamily: 'monospace' }}>{orderId}</span>
              </div>
              {searchParams.get('cardBrand') && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '5px 0', borderBottom: '1px solid #e2e8f0' }}>
                  <span style={{ color: '#64748b' }}>Card Type Used:</span>
                  <span style={{ fontWeight: 600, color: '#1e293b' }}>{searchParams.get('cardBrand')}</span>
                </div>
              )}
              {amount && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '5px 0', borderBottom: '1px solid #e2e8f0' }}>
                  <span style={{ color: '#64748b' }}>Attempted Amount:</span>
                  <span style={{ fontWeight: 700, color: '#0f172a' }}>${parseFloat(amount).toFixed(2)} {searchParams.get('currency') || 'USD'}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '5px 0', borderBottom: '1px solid #e2e8f0' }}>
                <span style={{ color: '#64748b' }}>Currency Type:</span>
                <span style={{ fontWeight: 600, color: '#1e293b' }}>{searchParams.get('currency') || 'USD'}</span>
              </div>
              {errorCode && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '5px 0' }}>
                  <span style={{ color: '#64748b' }}>Gateway Decline Code:</span>
                  <span style={{ fontWeight: 700, color: '#dc2626', fontFamily: 'monospace' }}>{errorCode}</span>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
            <button
              type="button"
              onClick={() => navigate('/talent/membership/select')}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: '8px',
                border: 'none',
                background: 'linear-gradient(135deg, #185FA5 0%, #0d386b 100%)',
                color: '#ffffff',
                fontWeight: 700,
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 4px 12px rgba(24, 95, 165, 0.25)'
              }}
            >
              <RefreshCw size={16} />
              <span>Try Again / Select Another Plan</span>
            </button>

            <Link
              to="/talent/membership"
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                backgroundColor: '#ffffff',
                color: '#475569',
                fontWeight: 600,
                fontSize: '13px',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                boxSizing: 'border-box'
              }}
            >
              <ArrowLeft size={16} />
              <span>Return to Membership Dashboard</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipFailed;
